// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useEffect, useState } from 'react';
import './Toast.css';

/**
 * Toast Notification Component
 * 
 * Displays temporary notification messages with auto-dismiss
 * 
 * @param {Object} props
 * @param {string} props.message - Toast message text
 * @param {string} props.type - Toast type: 'success', 'error', 'warning', 'info'
 * @param {number} props.duration - Auto-dismiss duration in ms (default: 3000)
 * @param {function} props.onClose - Callback when toast closes
 * @param {string} props.icon - Optional custom icon
 * @param {string} props.position - Position: 'top-right', 'top-center', 'bottom-right', 'bottom-center' (default: 'top-right')
 */
function Toast({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose,
  icon,
  position = 'top-right'
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300); // Match animation duration
  };

  if (!isVisible) return null;

  // Default icons for each type
  const defaultIcons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  };

  const toastIcon = icon || defaultIcons[type] || defaultIcons.info;

  return (
    <div 
      className={`toast toast-${type} toast-${position} ${isExiting ? 'toast-exit' : ''}`}
      onClick={handleClose}
      role="alert"
      aria-live="polite"
    >
      <div className="toast-icon">{toastIcon}</div>
      <div className="toast-message">{message}</div>
      <button 
        className="toast-close"
        onClick={handleClose}
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
}

export default Toast;
